import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, CreditCard, Building, Map, FileText } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости архитектурных решений
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Получение символа валюты
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      default: return '₽';
    }
  };

  const currencySymbol = getCurrencySymbol(formData.currencyType);

  // Функция форматирования валюты с нужным символом
  const formatCurrencyWithSymbol = (amount) => {
    return `${amount.toLocaleString('ru-RU')} ${currencySymbol}`;
  };

  return (
    <ToolResult title="Результаты расчета стоимости архитектурного проекта">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4"/>
            Итоговая стоимость
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrencyWithSymbol(results.totalCost)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Полная стоимость проекта с учетом всех выбранных параметров и услуг
          </p>
        </div>

        {/* Сроки реализации */}
        <div className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 p-5 border border-amber-100 dark:border-amber-800/50 shadow-sm">
          <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Срок выполнения
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
              {results.timelineWeeks} {formatWeeksLabel(results.timelineWeeks)}
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Расчетный срок выполнения всех работ при выбранном уровне срочности
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Тип проекта" 
          value={results.projectType} 
          color="indigo" 
          icon={<Building className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Площадь объекта" 
          value={`${formatNumber(results.buildingArea, 1)} м²`} 
          color="green" 
          icon={<FileText className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Этажность" 
          value={`${results.numFloors} ${formatFloorsLabel(results.numFloors)}`} 
          icon={<TrendingUp className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Сложность проекта" 
          value={results.complexity} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Местоположение" 
          value={results.location} 
          icon={<Map className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Этап проектирования" 
          value={results.projectStage} 
        />
        
        <ResultItem 
          label="Базовая стоимость проектирования" 
          value={formatCurrencyWithSymbol(results.baseCost)} 
          color="blue" 
        />
        
        {results.interiorDesignCost > 0 && (
          <ResultItem 
            label="Стоимость дизайна интерьера" 
            value={formatCurrencyWithSymbol(results.interiorDesignCost)} 
            color="purple" 
          />
        )}
        
        {results.engineeringSystemsCost > 0 && (
          <ResultItem 
            label="Стоимость инженерных систем" 
            value={formatCurrencyWithSymbol(results.engineeringSystemsCost)} 
            color="blue" 
          />
        )}
        
        {results.constructionDocumentationCost > 0 && (
          <ResultItem 
            label="Стоимость строительной документации" 
            value={formatCurrencyWithSymbol(results.constructionDocumentationCost)} 
            color="gray" 
          />
        )}
        
        {results.supervisionCost > 0 && (
          <ResultItem 
            label="Стоимость авторского надзора" 
            value={formatCurrencyWithSymbol(results.supervisionCost)} 
            color="amber" 
          />
        )}
        
        {results.visualizationCost > 0 && (
          <ResultItem 
            label="Стоимость визуализации" 
            value={formatCurrencyWithSymbol(results.visualizationCost)} 
            color="purple" 
          />
        )}

        {/* Информация о ставках */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Информация о расчете</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Базовая ставка за м² = <span className="font-medium text-green-600 dark:text-green-400">{formatNumber(results.baseRatePerSqm, 0)} {currencySymbol}/м²</span>. 
                При увеличении этажности, сложности и срочности применяются соответствующие коэффициенты.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "неделя" в зависимости от числа
const formatWeeksLabel = (weeks) => {
  const lastDigit = weeks % 10;
  const lastTwoDigits = weeks % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'недель';
  if (lastDigit === 1) return 'неделя';
  if (lastDigit >= 2 && lastDigit <= 4) return 'недели';
  return 'недель';
};

// Вспомогательная функция для склонения слова "этаж" в зависимости от числа
const formatFloorsLabel = (floors) => {
  const lastDigit = floors % 10;
  const lastTwoDigits = floors % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'этажей';
  if (lastDigit === 1) return 'этаж';
  if (lastDigit >= 2 && lastDigit <= 4) return 'этажа';
  return 'этажей';
};

export default ResultsOverview;